import {formatCurrency} from '@pinecast/common/helpers/format';
import {OptionButtonWithRadio} from '@pinecast/common/OptionButton';
import {DashboardTitle} from '@pinecast/common/Text';
import {amounts as globalAmounts} from '@pinecast/db-tip-jar-connect/currencies';
import type {
  DonationPage_podcast,
  DonationPage_tipJarSubscription,
} from '@pinecast/gql/__generated_tj__/DonationPage';
import {defineMessage, FormattedMessage} from '@pinecast/i18n';
import {useColors, useCSS} from '@pinecast/styles';

const messages = {
  labelSubscribe: defineMessage({
    description: 'Label for tip amount when you can only subscribe, not tip',
    defaultMessage: 'What amount would you like to contribute?',
  }),
  labelRecurring: defineMessage({
    description: 'Label for tip amount when a recurring tip already exists',
    defaultMessage:
      'What amount would you like to update your monthly subscription to?',
  }),
};

const usdAmounts = globalAmounts.usd;

const TipAmountSelection = ({
  onChange,
  podcast,
  subscription,
  value,
}: {
  onChange: (value: number) => void;
  podcast: DonationPage_podcast;
  subscription: DonationPage_tipJarSubscription | null;
  value: number | null;
}) => {
  const css = useCSS();
  const designColors = useColors();

  const amounts = [
    ...(globalAmounts[podcast.tipJarCurrency.toLowerCase()] ?? usdAmounts),
  ];
  if ((podcast.tipLimit ?? Infinity) < usdAmounts[5]) {
    amounts.pop();
  }
  if ((podcast.tipLimit ?? Infinity) < usdAmounts[4]) {
    amounts.pop();
  }

  return (
    <>
      <DashboardTitle>
        <FormattedMessage
          {...(subscription
            ? messages.labelRecurring
            : messages.labelSubscribe)}
        />
      </DashboardTitle>
      <div
        className={css({
          display: 'grid',
          gap: '12px',
          gridTemplateColumns: '1fr 1fr',
          padding: '0 0 24px',
        })}
        role="radiogroup"
      >
        {amounts.map(amount => {
          if (
            podcast.hasPrivateEpisodes &&
            podcast.canHaveRecurringTips &&
            podcast.privateAccessMinSubscription &&
            amount < podcast.privateAccessMinSubscription
          ) {
            return null;
          }
          return (
            <OptionButtonWithRadio
              alignInput="center"
              name="amount"
              selected={value === amount}
              key={amount}
              onClick={() => {
                onChange(amount);
              }}
            >
              <span
                className={css({
                  display: 'block',
                  fontSize: '20px',
                })}
              >
                {formatCurrency(amount, podcast.tipJarCurrency)}
              </span>
              {amount === subscription?.amount &&
                podcast.tipJarCurrency === subscription.currency && (
                  <span
                    className={css({
                      color: designColors.contentTextMuted,
                      display: 'block',
                      fontWeight: 400,
                    })}
                  >
                    <FormattedMessage
                      description="Label for the existing subscription's tip amount"
                      defaultMessage="Your current subscription"
                    />
                  </span>
                )}
            </OptionButtonWithRadio>
          );
        })}
      </div>
    </>
  );
};

export default TipAmountSelection;
